import * as React from 'react';
import { defaultMemoize } from 'reselect';
import { SpacesSiteUrls } from '../../app/spaces/SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '../../app/spaces/SpaceTelemetry';
import { explorerSubspace } from '../../app/spaces/SubspaceHelpers';
import { Subspace, TabbedSpace } from '../../app/spaces/TabbedSpace';
import { ISpaceInfoTypeItemCount } from '../../app/state/ContentCountsState';
import { DataRoleSubspaceName, SpaceInfo } from '../../app/state/SpaceInfo';
import { Messages } from '../../codegen/Localize';
import { ReactDirectiveProps } from '../../ts/directives/reactDirectiveFactory';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { IDataRoleDetailInfoWithUsage } from '../../ts/models/IDataRoleInfo';
import { FieldConceptIndexingErrorCode, FieldConceptIndexingStatusCode, GetFieldConceptIndexingStatusRequest, IGetFieldConceptIndexingStatusResult } from '../../ts/ServerApi/requests/GetFieldConceptIndexingStatus';
import { IDataRoleActions } from '../../ts/ServerApi/types/IDataRoleActions';
import { DataRoleSubspaceProps } from '../types/DataRoleSubspaceProps';
import { DefinitionSubspace } from './definition-subspace/DefinitionSubspace';
import { DataRoleSpaceHeader } from './header/DataRoleSpaceHeader';
import WebHttpClient from '../../httpClient/WebHttpClient';

export interface DataRoleSpaceStateProps {
  readonly spaceInfo: SpaceInfo;
  readonly urls: SpacesSiteUrls;
  readonly catalogingOnSiteEnabled: boolean;
  readonly contentCounts: ISpaceInfoTypeItemCount<'dataRole'>;
  readonly contentCountsUpToDate: boolean;
}

export interface DataRoleSpaceDispatchProps {
  readonly fetchUpdatedContentCounts: () => void;
}

export interface DataRoleSpaceOwnProps extends ReactDirectiveProps {
  readonly dataRoleDetailInfoWithUsage: IDataRoleDetailInfoWithUsage;
  readonly allowedActions: IDataRoleActions;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
  readonly subspaceProps: DataRoleSubspaceProps;
}

export interface DataRoleSpaceState {
  readonly fieldConceptIndexingStatusCode: FieldConceptIndexingStatusCode;
  readonly fieldConceptIndexingErrorCode: FieldConceptIndexingErrorCode;
}


export type DataRoleSpaceProps = DataRoleSpaceStateProps & DataRoleSpaceOwnProps & DataRoleSpaceDispatchProps;

function definitionSubspace(
  subspaceProps: DataRoleSubspaceProps,
  hasEditPermissions: boolean,
  fieldConceptIndexingStatusCode: FieldConceptIndexingStatusCode,
  fieldConceptIndexingErrorCode: FieldConceptIndexingErrorCode,
): Subspace {
  return {
    name: 'definition',
    tabLabel: Messages.definition(),
    renderContent: () => <DefinitionSubspace {...subspaceProps} hasEditPermissions={hasEditPermissions} fieldConceptIndexingStatusCode={fieldConceptIndexingStatusCode} fieldConceptIndexingErrorCode={fieldConceptIndexingErrorCode} />,
    shouldRenderTabContentCount: false
  };
}

function connectedFlowsSubspace(
  subspaceProps: DataRoleSubspaceProps,
  contentCounts: ISpaceInfoTypeItemCount<'dataRole'>,
  contentCountsUpToDate: boolean): Subspace {
  return explorerSubspace({
    name: 'connectedFlows',
    tabLabel: Messages.connectedFlows(),
    supportedDisplayModes: ['list'],
    angularContext: subspaceProps.angularContext,
    shouldRenderContentCount: true,
    contentCount: contentCounts && contentCounts['connectedFlows'],
    contentCountUpToDate: contentCountsUpToDate,
    toolbarVisibility: 'enabled',
    supportFilterPanel: true,
  });
}

function connectedDatasourcesSubspace(
  subspaceProps: DataRoleSubspaceProps,
  contentCounts: ISpaceInfoTypeItemCount<'dataRole'>,
  contentCountsUpToDate: boolean): Subspace {
  return explorerSubspace({
    name: 'connectedDatasources',
    tabLabel: Messages.connectedDatasources(),
    supportedDisplayModes: ['list'],
    angularContext: subspaceProps.angularContext,
    shouldRenderContentCount: true,
    contentCount: contentCounts && contentCounts['connectedDatasources'],
    contentCountUpToDate: contentCountsUpToDate,
    toolbarVisibility: 'enabled',
    supportFilterPanel: true,
  });
}

function getSubspaces(
  subspaceProps:  DataRoleSubspaceProps,
  contentCounts: ISpaceInfoTypeItemCount<'dataRole'>,
  contentCountsUpToDate: boolean,
  hasEditPermissions: boolean,
  fieldConceptIndexingStatusCode: FieldConceptIndexingStatusCode,
  fieldConceptIndexingErrorCode: FieldConceptIndexingErrorCode,
): ReadonlyArray<Subspace> {
  return [
    definitionSubspace(subspaceProps, hasEditPermissions, fieldConceptIndexingStatusCode, fieldConceptIndexingErrorCode),
    ...(FeatureFlags.semanticsServiceDataRoleConnectionsEnabled ? [connectedDatasourcesSubspace(subspaceProps, contentCounts, contentCountsUpToDate)] : [])
  ];
}

export class DataRoleSpace extends React.PureComponent<DataRoleSpaceProps, DataRoleSpaceState> {

  public state: DataRoleSpaceState = { fieldConceptIndexingStatusCode: 'INDEXING_STATUS_UNSPECIFIED', fieldConceptIndexingErrorCode: '' };
  private memoizedGetSubspaces = defaultMemoize(getSubspaces);
  constructor(props: DataRoleSpaceProps) {
    super(props);
  }

  public urlForSubspace = (subspaceName?: DataRoleSubspaceName): string => {
    switch(subspaceName) {
      case 'definition':
        return this.props.urls.dataRoleDefinitionUrl(this.props.dataRoleDetailInfoWithUsage.id);
      case 'connectedFlows':
        return this.props.urls.dataRoleConnectedFlowsUrl(this.props.dataRoleDetailInfoWithUsage.id);
      case 'connectedDatasources':
        return this.props.urls.dataRoleConnectedDatasourcesUrl(this.props.dataRoleDetailInfoWithUsage.id);
      default:
        return this.props.urls.dataRoleUrl(this.props.dataRoleDetailInfoWithUsage.id);
    }
  }

  private updateFieldConceptIndexingStatus = async () => {
    const statusRequest = new GetFieldConceptIndexingStatusRequest({field_concept_uri: this.props.dataRoleDetailInfoWithUsage.fieldConceptUri});
    const status: IGetFieldConceptIndexingStatusResult = await WebHttpClient.sendRequest(statusRequest);
    if(status.fieldConceptIndexingStatusCode === undefined) { return; }
    this.setState({ fieldConceptIndexingStatusCode: status.fieldConceptIndexingStatusCode, fieldConceptIndexingErrorCode: status.fieldConceptIndexingErrorCode });
    // If the status indicates that indexing is still in progress, we should send the request again later.
    if(retryStatusCodes.indexOf(status.fieldConceptIndexingStatusCode) > -1) {
      setTimeout(this.updateFieldConceptIndexingStatus, 10000);
    }
  }

  public render() {
    const subspaces = this.memoizedGetSubspaces(
      this.props.subspaceProps,
      this.props.contentCounts,
      this.props.contentCountsUpToDate,
      this.props.allowedActions.setName,
      this.state.fieldConceptIndexingStatusCode,
      this.state.fieldConceptIndexingErrorCode,
    );

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.spaceInfo.subspaceName}
        urlForSubspace={this.urlForSubspace}
        mastheadElement={this.mastheadElement}
        testIdPrefix={'data-role'}
        spaceItemName={this.props.dataRoleDetailInfoWithUsage.name}
      />
    );
  }

  public componentDidMount() {
    this.handleTelemetry();
    this.props.fetchUpdatedContentCounts();
    this.updateFieldConceptIndexingStatus();
  }

  public componentDidUpdate(prevProps: DataRoleSpaceProps, prevState: DataRoleSpaceState) {
    this.props.fetchUpdatedContentCounts();
    if (this.props.dataRoleDetailInfoWithUsage.id !== prevProps.dataRoleDetailInfoWithUsage.id) {
      this.handleTelemetry();
    }
    if (retryStatusCodes.indexOf(prevState.fieldConceptIndexingStatusCode) > -1 && retryStatusCodes.indexOf(prevState.fieldConceptIndexingStatusCode) === -1) {
      location.reload(); // When we finish loading, refresh and get the latest list of fields
    }
  }

  private handleTelemetry() {
    maybeSendTelemetry_favoriteItemOpened(this.props.dataRoleDetailInfoWithUsage);
  }

  private get mastheadElement(): React.ReactNode {
    return <DataRoleSpaceHeader
      availableActions={this.props.allowedActions}
      item={this.props.dataRoleDetailInfoWithUsage}
      urls={this.props.urls}
      addToFavorites={this.props.addToFavorites}
      removeFromFavorites={this.props.removeFromFavorites}
      fieldConceptIndexingStatusCode={this.state.fieldConceptIndexingStatusCode}
      fieldConceptIndexingErrorCode={this.state.fieldConceptIndexingErrorCode}
    />;
  }
}

// If computation is still in progress, we should update periodically to get the latest status
const retryStatusCodes: ReadonlyArray<FieldConceptIndexingStatusCode> = [
  'INDEXING_IN_PROGRESS',
  'INDEXING_PENDING',
  'INDEXING_FAILED_RETRY_PENDING',
];
